/**
 * THE Attested renderer — every money figure, without exception.
 *
 * Attestation is a WORD, not a glyph family (D17): `broker` · `engine` ·
 * `estimated` · `pending` · `abandoned`. An unknown P&L is a BAND, never a
 * zero and never a hole (§6.8 rule 9), and an estimate never produces a
 * verdict — it produces `≈`.
 *
 * Colour law: up/down are DATA-ONLY semantics (#1FB873 / #E5484D). Signal
 * Amber never appears here. Amber has exactly one referent in this product —
 * money at risk RIGHT NOW — and a settled P&L is not that.
 */

import type { Attestation, Attested } from "./api";

export const ATTESTATION_WORD: Record<Attestation, string> = {
  BROKER: "broker",
  ENGINE: "engine",
  ESTIMATED: "estimated",
  PENDING: "pending",
  ABANDONED: "abandoned",
  NONE: "—",
};

const ATTESTATION_TITLE: Record<Attestation, string> = {
  BROKER: "venue-attested realized P&L (broker_history)",
  ENGINE: "engine close-confirm",
  ESTIMATED: "derived from the trade's own SL/TP distance — never a verdict",
  PENDING: "the venue never reported a close price; backfill has not landed",
  ABANDONED: "the evidence ladder bottomed out",
  NONE: "no P&L concept applies to this row",
};

export function money(v: number | null | undefined, unit: "usd" | "pct" = "usd"): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  const sign = v < 0 ? "−" : "+";
  const a = Math.abs(v);
  return unit === "pct" ? `${sign}${a.toFixed(2)}%` : `${sign}$${a.toFixed(2)}`;
}

export function num(v: number | null | undefined, digits = 2): string {
  return v === null || v === undefined || !Number.isFinite(v) ? "—" : v.toFixed(digits);
}

export function signClass(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v) || v === 0) return "";
  return v > 0 ? "up" : "down";
}

/** The one renderer for a money figure. A caller that has only a raw float
 *  has to construct an Attested first, which is the point.
 *
 *  `terse` omits the server's prose NOTE — and only the note. It exists for
 *  the LEDGER row, where the P&L cell is a fixed 8 rem column in a
 *  ten-column grid: a sentence there elided to `— p..`, which is not an
 *  honest rendering of anything, and before it was clipped it blew the
 *  column out and destroyed the alignment of the decimal points down the
 *  whole list. Nothing is lost by it. The note's own fact ("position still
 *  open") is already on that row twice — `open` in the duration column and
 *  `evidence NONE` in the evidence column — and the note itself is printed
 *  in full, by this same component, on the ORDER surface the row links to.
 *  The VALUE, the ATTESTATION WORD and the BOUND are never terse. */
export function Money({
  a,
  big = false,
  terse = false,
}: {
  a: Attested | null | undefined;
  big?: boolean;
  terse?: boolean;
}) {
  if (!a) return <span className="dim">—</span>;
  const note = terse ? null : a.note;
  const approx = a.attestation === "ESTIMATED";
  if (a.value === null) {
    return (
      <span className="attested" data-attestation={a.attestation}>
        {/* Both sub-columns are emitted, in the same order and with the same
            widths as the value branch — `.mono` then `.attn`. That is what
            keeps a no-P&L row from shifting the decimal points of every row
            around it: with only one child, flex-end pulled the placeholder
            80 px right of the column the figures live in. The attestation is
            not padding, it is the LABEL ON THE HOLE — `pending`, `abandoned`
            — and it now reads exactly like `+$47.22 broker` above it. */}
        <span className="mono dim">—</span>
        <span className="attn" title={ATTESTATION_TITLE[a.attestation]}>
          {ATTESTATION_WORD[a.attestation]}
        </span>
        {a.bound && (
          <span className="band-chip" title={a.bound.basis}>
            band [{money(a.bound.low)} … {money(a.bound.high)}]
          </span>
          )}
        {note && (
          <span className="span__note" title={note}>
            {note}
          </span>
          )}
      </span>
    );
  }
  return (
    <span className={`attested${big ? " attested--big" : ""}`} data-attestation={a.attestation}>
      <span className={`mono ${signClass(a.value)}`}>
        {approx ? "≈ " : ""}
        {money(a.value, a.unit)}
      </span>
      <span className="attn" title={ATTESTATION_TITLE[a.attestation]}>
        {ATTESTATION_WORD[a.attestation]}
      </span>
      {note && (
        <span className="span__note" title={note}>
          {note}
        </span>
        )}
    </span>
  );
}

/** Close evidence is a RENDERED COLUMN everywhere a P&L appears (§2.4). */
/** `compact` drops the "evidence" prefix for use inside the ledger's evidence
 *  COLUMN, where the word is already carried by the column itself. It is not
 *  cosmetic: the vocabulary runs to PRICE_INFERRED and ACCOUNT_LEVEL, and with
 *  the prefix the chip overflowed its grid track and clipped mid-token —
 *  "evidence DEAL_TI", which reads as a different (nonexistent) verdict. */
export function CloseEvidence({
  evidence,
  compact = false,
}: {
  evidence: string | null | undefined;
  compact?: boolean;
}) {
  const e = evidence ?? "NONE";
  const bad = e === "NONE" || e === "PRICE_INFERRED";
  return (
    <span className={`chip${bad ? " chip--down" : ""}`} title="how we proved the position closed">
      {compact ? e : `evidence ${e}`}
    </span>
  );
}

/** Pairing is a rendered column, ALWAYS. `inferred` means the chronological
 *  open→terminal walk was used, which is valid only while R3 guarantees one
 *  position at a time — and it has already cost real money once (the 26
 *  phantom opens of 2026-06-29). */
export function Pairing({ pairing }: { pairing: string | null | undefined }) {
  const inferred = pairing === "inferred";
  return (
    <span
      className={`chip${inferred ? " chip--dotted" : ""}`}
      title={
        inferred
          ? "no trade_id on these rows — paired by the chronological walk, valid only under the R3 one-position invariant"
          : "the whole lifecycle shares one trade_id"
      }
    >
      pairing {pairing ?? "—"}
    </span>
  );
}
